import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import StandardizedChip from '../common/StandardizedChip';
import {
  Security as SecurityIcon,
  Policy as PolicyIcon,
  Shield as ShieldIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { apiFetch } from '../../config/api';
import PolicyDetails from './PolicyDetails';
import { debugError } from '../../utils/debugLog';

/**
 * Supported Policies Component
 *
 * Displays all supported content checking policies and their details.
 * This is the main interface for viewing and testing policy configurations.
 */
const SupportedPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);

      // Use new content-check API to get domains and service info
      const [domainsResponse, serviceResponse] = await Promise.all([
        apiFetch('/api/v1/content-check/domains'),
        apiFetch('/api/v1/content-check/service-info'),
      ]);

      if (domainsResponse.success && serviceResponse.initialized) {
        // Transform domains into policy-like structure for the UI
        const policies = domainsResponse.domains.map(domain => ({
          name: `${domain.name}-policy`,
          description:
            domain.description || `Content checking for ${domain.name} domain`,
          enabled: domain.status === 'available',
          version: '1.0.0',
          rule_count: domain.supported_entities?.length || 0,
          created_at: new Date().toISOString(),
          supported_entities: domain.supported_entities || [],
          data_fields: domain.data_fields || [],
          status: domain.status,
          memory_usage: domain.memory_usage,
        }));

        setPolicies(policies);
        setError(null); // Clear any previous errors
      } else {
        setError('Failed to fetch content check domains');
      }
    } catch (err) {
      debugError('❌ Error fetching content check domains:', err);
      setError(`Error loading content check domains: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = policy => {
    setSelectedPolicy(policy);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedPolicy(null);
  };

  const getPolicyIcon = policyName => {
    switch (policyName) {
      case 'pii-policy':
        return <SecurityIcon color="primary" />;
      case 'hipaa-policy':
        return <ShieldIcon color="success" />;
      case 'financial-policy':
        return <PolicyIcon color="warning" />;
      default:
        return <PolicyIcon />;
    }
  };

  const getPolicyStatus = policy => {
    if (policy.status === 'available') {
      return <StandardizedChip label="Available" color="success" />;
    } else if (policy.status === 'maintenance') {
      return <StandardizedChip label="Maintenance" color="warning" />;
    } else {
      return <StandardizedChip label="Unavailable" color="error" />;
    }
  };

  const getComplianceLevel = policyName => {
    switch (policyName) {
      case 'pii-policy':
        return { level: 'High', color: 'error', icon: <SecurityIcon /> };
      case 'hipaa-policy':
        return { level: 'Critical', color: 'error', icon: <ShieldIcon /> };
      case 'financial-policy':
        return { level: 'High', color: 'warning', icon: <PolicyIcon /> };
      default:
        return { level: 'Medium', color: 'default', icon: <PolicyIcon /> };
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Supported Policies
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View and test all supported content checking policies and their
        configurations.
      </Typography>

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Policy Overview" />
          <Tab label="Detailed List" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          {policies.map(policy => {
            const compliance = getComplianceLevel(policy.name);
            return (
              <Grid
                size={{ xs: 12, md: 6, lg: 4 }}
                key={policy.name}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  minWidth: 0,
                  flexBasis: '33.333%',
                  maxWidth: '33.333%',
                }}
              >
                <Card
                  onClick={() => handleViewDetails(policy)}
                  sx={{
                    width: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    height: '100%',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: 4,
                      transform: 'translateY(-2px)',
                      transition: 'all 0.2s ease-in-out',
                    },
                  }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      minHeight: '300px',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                      width: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Main Content */}
                    <Box>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Box display="flex" alignItems="center">
                          {getPolicyIcon(policy.name)}
                          <Typography variant="h6" sx={{ ml: 1 }}>
                            {policy.name.replace('-policy', '').toUpperCase()}
                          </Typography>
                        </Box>
                        {getPolicyStatus(policy)}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {policy.description}
                      </Typography>

                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        mb={2}
                      >
                        <Box>
                          <Typography variant="body2">
                            Entities: {policy.rule_count || 0}
                          </Typography>
                          {policy.supported_entities && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {policy.supported_entities.slice(0, 3).join(', ')}
                              {policy.supported_entities.length > 3 &&
                                ` +${policy.supported_entities.length - 3} more`}
                            </Typography>
                          )}
                        </Box>
                        <StandardizedChip
                          label={compliance.level}
                          color={compliance.color}
                          icon={compliance.icon}
                          width={100}
                        />
                      </Box>
                    </Box>

                    {/* Footer - Button always at bottom */}
                    <Button
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={e => {
                        e.stopPropagation();
                        handleViewDetails(policy);
                      }}
                      fullWidth
                      sx={{ mt: 'auto' }}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {activeTab === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Policy Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Entities</TableCell>
                <TableCell>Compliance Level</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {policies.map(policy => {
                const compliance = getComplianceLevel(policy.name);
                return (
                  <TableRow key={policy.name}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getPolicyIcon(policy.name)}
                        <Typography variant="body2" sx={{ ml: 1 }}>
                          {policy.name.replace('-policy', '').toUpperCase()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getPolicyStatus(policy)}</TableCell>
                    <TableCell>{policy.rule_count || 0}</TableCell>
                    <TableCell>
                      <StandardizedChip
                        label={compliance.level}
                        color={compliance.color}
                        icon={compliance.icon}
                        width={100}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewDetails(policy)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Policy Details Dialog */}
      <PolicyDetails
        policy={selectedPolicy}
        open={detailsOpen}
        onClose={handleCloseDetails}
      />
    </Box>
  );
};

export default SupportedPolicies;
